<script setup>
import { ref, reactive, defineEmits, onMounted } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanKeuanganService from '@/services/LaporanKeuanganService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanKeuanganService()
let isLoadingPrint = ref(false)
let isLoadingExport = ref(false)
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let form = reactive({
  date_from: null,
  date_to: null,
  transaction_type: null,
})

const list_transaction_type = [
  { label: 'Jurnal Umum', value: 'Jurnal Umum' },
  { label: 'Kas Keluar', value: 'Kas Keluar' },
  { label: 'Kas Masuk', value: 'Kas Masuk' },
  { label: 'Kas Transfer', value: 'Kas Transfer' },
  { label: 'Purchase Invoice', value: 'Purchase Invoice' },
  { label: 'Purchase Payment', value: 'Purchase Payment' },
  { label: 'Purchase Return', value: 'Purchase Return' },
  { label: 'Sales Invoice', value: 'Sales Invoice' },
  { label: 'Sales Payment', value: 'Sales Payment' },
  { label: 'Sales Return', value: 'Sales Return' },
]

let submitted = ref(false)

const rules = {
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {})

function onExport(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingExport.value = true
  
  laporanService
    .exportLaporanJurnal({
      'filter[transaction_type]': form.transaction_type,
      'filter[periode_from]': dayjs(form.date_from).format('YYYY-MM-DD'),
      'filter[periode_to]': dayjs(form.date_to).format('YYYY-MM-DD'),
    })
    .then((res) => {
      isLoadingExport.value = false
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'laporan-keseluruhan-jurnal.xlsx') //khusus ini pakai xlsx
      document.body.appendChild(link)
      link.click()
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Keseluruhan Jurnal', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrint(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingPrint.value = true

  laporanService
    .printLaporanJurnal({
      'filter[transaction_type]': form.transaction_type,
      'filter[periode_from]': dayjs(form.date_from).format('YYYY-MM-DD'),
      'filter[periode_to]': dayjs(form.date_to).format('YYYY-MM-DD'),
    })
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)

      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'jurnal.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })
    .catch((err) => {
      errorHandler(err, 'Data Jurnal Keseluruhan', toast)
    })
    .finally(() => (isLoadingPrint.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false
  form.transaction_type = null
  form.date_from = null
  form.date_to = null
}
</script>

<template>
  <fx-report-container
    title="Laporan Keseluruhan Jurnal"
    :loading="isLoadingPrint"
    @toggle="onToggle"
  >
    <template #content>
      <form @submit.prevent="search(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-3">
            <Dropdown
              v-model="form.transaction_type"
              :options="list_transaction_type"
              optionValue="value"
              optionLabel="label"
              placeholder="Pilih Jenis Transaksi"
              class="w-full"
              showClear
            />
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-4 xl:col-5">
            <Button
              :loading="isLoadingPrint"
              type="button"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mb-0 md:mb-2 lg:mb-0"
              @click="onPrint(!v$.$invalid)"
            />
            <Button
              :loading="isLoadingExport"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2 md:ml-0 lg:ml-2 mb-0 md:mb-2 lg:mb-0"
              @click="onExport(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 md:ml-0 xl:ml-2 lg:mt-2 xl:mt-0"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
      <Dialog
        :header="pdfTitle"
        v-model:visible="dialogViewer"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
        :style="{ width: '90vw' }"
        :modal="true"
      >
        <div>
          <embed
            :src="pdfSource"
            type="application/pdf"
            width="100%"
            height="600px"
          />
        </div>
        <template #footer>
          <Button
            type="button"
            label="Download"
            icon="pi pi-download"
            class="p-button-text mr-2"
            @click="onDownloadPdf"
          />
          <Button
            type="button"
            label="Print"
            icon="pi pi-print"
            @click="onPrintPdf"
          />
        </template>
      </Dialog>
    </template>
  </fx-report-container>
</template>
